/**
 * New Item Form Component
 *
 * Form for adding a new catalog item with packaging selection and auto-conversion.
 */

import { useMemo, useEffect } from 'react';
import { Check, X, Info, AlertTriangle } from 'lucide-react';
import { Button, Input, Select, Badge } from '@/components/ui';
import { getConversionInfo, ConversionMode } from '@bstock/shared';
import { formatQuantity, getQuantityStep } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { PackagingOption } from '@bstock/shared';
import type { NewItemState } from './catalog.types';

interface NewItemFormProps {
  newItem: NewItemState;
  packagings: PackagingOption[];
  currency: string;
  onPackagingSelect: (packagingId: string) => void;
  onFieldChange: (field: keyof NewItemState, value: string) => void;
  onCancel: () => void;
  onCreate: () => void;
  isLoading: boolean;
}

export function NewItemForm({
  newItem,
  packagings,
  currency,
  onPackagingSelect,
  onFieldChange,
  onCancel,
  onCreate,
  isLoading,
}: NewItemFormProps) {
  // Get selected packaging details for display
  const selectedPackaging = packagings.find((p) => p.id === newItem.packagingId);

  // Calculate conversion info based on packaging and raw product
  const conversionInfo = useMemo(() => {
    if (!selectedPackaging) return null;

    return getConversionInfo(
      selectedPackaging.quantity,
      selectedPackaging.unit,
      newItem.rawProduct.recipeUnit
    );
  }, [selectedPackaging, newItem.rawProduct.recipeUnit]);

  // Synchronize auto-calculated value with state
  useEffect(() => {
    if (
      conversionInfo &&
      (conversionInfo.mode === ConversionMode.AUTO || conversionInfo.mode === ConversionMode.NONE) &&
      conversionInfo.value !== null
    ) {
      onFieldChange('conversionToRecipeUnit', String(conversionInfo.value));
    }
  }, [conversionInfo, onFieldChange]);

  return (
    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-medium text-primary-900">{newItem.rawProduct.name}</h3>
          <p className="text-xs text-primary-700">
            SKU: {newItem.rawProduct.sku} | Unite: {newItem.rawProduct.recipeUnit.symbol}
          </p>
        </div>
        <Badge variant="primary">Nouveau</Badge>
      </div>

      <div className="grid grid-cols-5 gap-3">
        {/* Conditionnement Select */}
        <div>
          <Select
            label="Conditionnement *"
            value={newItem.packagingId ?? ''}
            onChange={onPackagingSelect}
            options={packagings.map((p) => ({
              value: p.id,
              label: `${p.name} (${formatQuantity(p.quantity)} ${p.unit.symbol})`,
            }))}
            placeholder="Selectionner..."
          />
        </div>

        {/* Unite - affichee automatiquement */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Contenu</label>
          <Input
            value={selectedPackaging ? `${formatQuantity(selectedPackaging.quantity)} ${selectedPackaging.unit.symbol}` : ''}
            disabled
            className="bg-neutral-100"
          />
        </div>

        <div>
          <Input
            label={`Conversion (${newItem.rawProduct.recipeUnit.symbol}) *`}
            type="number"
            step={getQuantityStep()}
            min="0.01"
            value={newItem.conversionToRecipeUnit}
            onChange={(e) => onFieldChange('conversionToRecipeUnit', e.target.value)}
            placeholder="24"
            disabled={conversionInfo ? !conversionInfo.isEditable : false}
            className={conversionInfo && !conversionInfo.isEditable ? 'bg-neutral-50 cursor-not-allowed' : ''}
          />
          {/* Conversion info message */}
          {conversionInfo && (
            <div
              className={cn(
                'mt-1 text-xs flex items-start gap-1',
                conversionInfo.mode === ConversionMode.AUTO && 'text-success-600',
                conversionInfo.mode === ConversionMode.NONE && 'text-neutral-500',
                conversionInfo.mode === ConversionMode.MANUAL && 'text-warning-600'
              )}
            >
              {conversionInfo.mode === ConversionMode.AUTO && (
                <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
              )}
              {conversionInfo.mode === ConversionMode.NONE && (
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              )}
              {conversionInfo.mode === ConversionMode.MANUAL && (
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              )}
              <span>{conversionInfo.message}</span>
            </div>
          )}
        </div>
        <Input
          label={`Prix HT (${currency}) *`}
          type="number"
          step="0.01"
          min="0"
          value={newItem.priceExclTax}
          onChange={(e) => onFieldChange('priceExclTax', e.target.value)}
          placeholder="25.00"
        />
        <Input
          label="Ref. fournisseur"
          value={newItem.supplierRef}
          onChange={(e) => onFieldChange('supplierRef', e.target.value)}
          placeholder="Code article"
        />
      </div>

      <div className="flex items-center justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isLoading}>
          <X className="w-4 h-4 mr-1" />
          Annuler
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onCreate}
          isLoading={isLoading}
          disabled={
            !newItem.packagingId ||
            !newItem.conversionToRecipeUnit ||
            !newItem.priceExclTax
          }
        >
          <Check className="w-4 h-4 mr-1" />
          Ajouter
        </Button>
      </div>
    </div>
  );
}
